import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import swal from 'sweetalert';
import { getGroupTours } from '../../api/groupToursApi';
import { createGroupTourismInterest } from '../../api/groupTourismApi';
import { getCurrencyRate } from '../../api/paymentApi';
import { getCardVisibilityState, slotSortOrder } from '../../helpers/getCardVisibilityState';
import { buildMonthRange } from '../../helpers/buildMonthRange';
import { BASE_URL } from '../../constants';
import GroupTourCard from '../groupTours/GroupTourCard';
import { PaymentMethodModal } from '../payments';

/** Partner-created group trips (not platform journey departures). */
function GroupVendorDepartures() {
	const currentUser = useSelector((state) => state.userStore?.currentUser) || {};
	const [tours, setTours] = useState([]);
	const [loading, setLoading] = useState(true);
	const [selectedIdx, setSelectedIdx] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	const [usdToNprRate, setUsdToNprRate] = useState(null);
	const [showPaymentModal, setShowPaymentModal] = useState(false);
	const [paymentModalData, setPaymentModalData] = useState(null);

	useEffect(() => {
		const months = buildMonthRange(12).join(',');
		setLoading(true);
		getGroupTours({ status: 'available', year_month: months, per_page: 40 })
			.then((res) => {
				const list = (res.data?.group_tours || []).filter((t) => !t.platform_owned && !t.journey_slug);
				setTours(list.sort((a, b) => slotSortOrder(a, b)));
			})
			.catch(() => setTours([]))
			.finally(() => setLoading(false));
		getCurrencyRate()
			.then((r) => setUsdToNprRate(Number(r.data?.usd_to_npr) || null))
			.catch(() => setUsdToNprRate(null));
	}, []);

	const handleSubmitInterest = (tour, values) => {
		setSubmitting(true);
		const payload = { ...values, group_tour_id: tour.idx };
		if (!payload.number_of_people) delete payload.number_of_people;
		return createGroupTourismInterest(payload)
			.then((res) => {
				setSelectedIdx(null);
				const pay = (res.data && res.data.payment) || res.data;
				const idx = pay && pay.idx;
				const amount = (pay && pay.amount) != null ? pay.amount : tour.interest_amount;
				if (idx != null && amount != null) {
					setPaymentModalData({
						productIdentity: idx,
						productName: 'GROUP_TOURISM',
						productUrl: `${BASE_URL || ''}/group-trips`,
						amount: Number(amount),
						currencyCode: tour.currency_code || 'NPR',
					});
					setShowPaymentModal(true);
				} else {
					swal('Error', 'Could not open payment.', 'error');
				}
			})
			.catch((err) => {
				if (err.response?.status === 401) {
					window.location.href = `/login?return=${encodeURIComponent('/group-trips')}`;
					throw err;
				}
				const msg = (err.response?.data?.errors || []).join(' ')
					|| err.response?.data?.error
					|| 'Could not register. Please try again.';
				swal('Registration failed', msg, 'error');
				throw err;
			})
			.finally(() => setSubmitting(false));
	};

	if (loading || tours.length === 0) return null;

	return (
		<section className="group-tour-cards-section gd-vendor-departures" aria-label="Partner group trips">
			<h2 className="gd-vendor-departures__title">More group trips from local partners</h2>
			<p className="gd-vendor-departures__lead">
				Hand-picked departures from verified operators — reserve your seat with a deposit, same as platform trips.
			</p>
			<div className="group-tour-cards">
				{tours.map((tour) => {
					const vis = getCardVisibilityState(tour);
					const canReg = !vis.disabled;
					return (
						<GroupTourCard
							key={tour.idx}
							tour={tour}
							variant="public"
							isSelected={selectedIdx === tour.idx}
							onSelect={(t) => setSelectedIdx((prev) => (prev === t.idx ? null : t.idx))}
							onSubmitInterest={canReg ? handleSubmitInterest : undefined}
							submitting={submitting}
							currentUser={currentUser}
							visibilityState={vis}
							usdToNprRate={usdToNprRate}
						/>
					);
				})}
			</div>
			<PaymentMethodModal
				open={showPaymentModal}
				onClose={() => { setShowPaymentModal(false); setPaymentModalData(null); }}
				paymentParams={paymentModalData}
				amount={paymentModalData?.amount}
			/>
		</section>
	);
}

export default GroupVendorDepartures;
